import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { emptyClassesContent } from '@app/schools/school-page/school-page.variables';
import { fullUserServiceDetails, ServicesBlockType, UserServicesService } from '@core/services';
import { HelpersBffService } from '@core/services/bff/helpers.service';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { loadMe } from '@store/actions/profile.actions';
import { getMe } from '@store/reducers/profile.reducer';
import {
  CSchool,
  ESchoolParticipantRole,
  FindUserServiceStrategyEnum,
  IFindUserServicesFilter,
  ITeachingSubject,
  MediaFormatEnum,
  User,
  UserService,
  UserServiceDetailsType,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-school-classes',
  templateUrl: './school-classes.component.html',
  styleUrls: ['./school-classes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolClassesComponent extends ChangableComponent implements OnInit {
  // DI
  private store = inject(Store);
  private userServicesService = inject(UserServicesService);
  public deviceService = inject(DeviceDetectorService);
  private location = inject(Location);
  private helpers = inject(HelpersBffService);

  @Input() school: CSchool;
  @Input() public set subjectId(id: number) {
    this._subjectId = id;
    this.loadServices();
  }

  public get subjectId() {
    return this._subjectId;
  }

  @Output() public loaded = new EventEmitter<boolean>();

  public _me: User;
  public _subjectId: number;
  public selectedService: UserService;
  public teachingSubjects: ITeachingSubject[] = [];
  private _onChange: Subscription;
  private originalUrl: string;
  public openedUserService: UserService;
  public userServiceCardFullInModal: boolean;
  public surfaceBlockDisplay: boolean;
  public emptyClassesContent = emptyClassesContent;
  public services: ServicesBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {
      use: FindUserServiceStrategyEnum.school,
    },
    pagination: {
      page: 1,
      pageSize: 10,
    },
  };

  ngOnInit() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.store.dispatch(loadMe());
        },
      });
    this.teachingSubjects = this.setTeachingSubjects();
    this.loadServices();
  }

  public get meIsOwnerSchool(): boolean {
    return !!this.school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.owner)
      .find((_part) => _part.account_id === this._me?.id);
  }

  private loadServices() {
    this.services.loading = true;
    this.detectChanges();

    const filter: Partial<IFindUserServicesFilter> = {
      ...this.services.filter,
      school_id: this.school?.id,
      subject_id: this.subjectId,
    };

    const details: UserServiceDetailsType[] = [...fullUserServiceDetails, 'school', 'school:cover'];

    this.userServicesService
      .getServices(filter, this.services.pagination, details)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.services.items = response.results;
          this.services.pagination = response.pagination;
          this.services.loaded = true;
          this.services.loading = false;
          this.loaded.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.services.loading = false;
          this.detectChanges();
        },
      });
  }

  public trackUserServiceByFn(userService: UserService): string {
    return userService.id;
  }

  public onSelect(service: UserService) {
    this.selectedService = service;
    this.selectedService.school = this.school;
  }

  public onRemove(e: boolean) {
    if (e) {
      this.onChangeUserService();
    }
  }

  public onChangeUserService() {
    this.selectedService = null;
    this.detectChanges();

    this.clearTimeout(this._onChange);
    this._onChange = this.setTimeout(() => {
      this.loadServices();
    }, 3000);
  }

  public onCancelSchoolService() {
    this.selectedService = null;
  }

  public onToggleUserServiceInModal(userService: UserService = null) {
    this.openedUserService = userService;
    this.userServiceCardFullInModal = !this.userServiceCardFullInModal;
    this.setAndResetUrl();
    this.detectChanges();
  }

  public onIinViewportChange(e) {
    this.surfaceBlockDisplay = e;
    this.detectChanges();
  }

  private setAndResetUrl() {
    const url = this.helpers.userServiceRouteUniversal(this.openedUserService);
    const currentUrl = this.location.path(false);
    if (this.userServiceCardFullInModal) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  private setTeachingSubjects(): ITeachingSubject[] {
    return this.school?.subjects.map((_s) => ({
      subject_id: _s.id,
      subject: _s,
      subject_other_name: null,
      subject_other_id: null,
      description: '',
      video_url: '',
      video_id: '',
      video: {
        format: MediaFormatEnum.original,
        url: '',
      },
    }));
  }
}
